import React from 'react';
import _ from 'lodash';
import { INamedEntityRecognitionRelationshipDefinition } from '@scale/shared/types/params/namedentityrecognition';
import { INamedEntityRecognitionResponseRelationship } from '@scale/shared/types/response/namedentityrecognition';
import type {
  INamedEntityRecognitionAnnotation,
  INamedEntityRecognitionRelationship,
} from './namedEntityRecognitionAnnotations';
import { INamedEntityRecognitionLabel } from './namedEntityRecognitionLabels';
import { isValidRelationshipForLabels } from '@scale/shared/namedentityrecognition/linking';
import uuid from 'uuid';

export function augmentResponseRelationship(
  relationship: INamedEntityRecognitionResponseRelationship,
  annotations: INamedEntityRecognitionAnnotation[],
  relationshipDefinition: INamedEntityRecognitionRelationshipDefinition,
): INamedEntityRecognitionRelationship | null {
  const sourceAnnotation = annotations.find(({ id }) => id === relationship.source_ref);
  const targetAnnotation = annotations.find(({ id }) => id === relationship.target_ref);
  if (!sourceAnnotation || !targetAnnotation) {
    return null;
  }

  if (
    sourceAnnotation.startLineIdx < targetAnnotation.startLineIdx ||
    (sourceAnnotation.startLineIdx === targetAnnotation.startLineIdx &&
      sourceAnnotation.startOffset < targetAnnotation.startOffset)
  ) {
    return {
      ...relationship,
      sourceAnnotation,
      targetAnnotation,
      topAnnotation: sourceAnnotation,
      bottomAnnotation: targetAnnotation,
      relationshipDefinition,
    };
  } else {
    return {
      ...relationship,
      sourceAnnotation,
      targetAnnotation,
      topAnnotation: targetAnnotation,
      bottomAnnotation: sourceAnnotation,
      relationshipDefinition,
    };
  }
}

export function useNamedEntityRecognitionLinkingHelpers(
  relationshipDefinitions: INamedEntityRecognitionRelationshipDefinition[],
  labels: INamedEntityRecognitionLabel[],
) {
  const isValidRelationshipForAnnotationPair = React.useCallback(
    (
      relationship: INamedEntityRecognitionRelationshipDefinition,
      sourceAnnotation: INamedEntityRecognitionAnnotation,
      targetAnnotation: INamedEntityRecognitionAnnotation,
    ) => {
      if (sourceAnnotation.id === targetAnnotation.id) {
        return false;
      }

      return isValidRelationshipForLabels(
        relationship,
        sourceAnnotation.labelId,
        targetAnnotation.labelId,
        labels,
      );
    },
    [labels],
  );

  const getValidRelationshipsForAnnotationPair = React.useCallback(
    (
      sourceAnnotation: INamedEntityRecognitionAnnotation,
      targetAnnotation: INamedEntityRecognitionAnnotation,
    ): INamedEntityRecognitionRelationship[] => {
      if (sourceAnnotation.id === targetAnnotation.id) {
        return [];
      }

      return _.flatMap(relationshipDefinitions, relationship => {
        const nameObj = relationship.name ? { name: relationship.name } : {};
        const possibilities: INamedEntityRecognitionRelationship[] = [];
        if (
          isValidRelationshipForAnnotationPair(relationship, sourceAnnotation, targetAnnotation)
        ) {
          possibilities.push(
            augmentResponseRelationship(
              {
                id: uuid.v4(),
                source_ref: sourceAnnotation.id,
                target_ref: targetAnnotation.id,
                // Assigning the name field in this rounadbout fashion lets us keep it prevent adding it
                // at all when name is undefined.
                ...nameObj,
              },
              [sourceAnnotation, targetAnnotation],
              relationship,
            )!,
          );
        }
        if (
          isValidRelationshipForAnnotationPair(relationship, targetAnnotation, sourceAnnotation)
        ) {
          possibilities.push(
            augmentResponseRelationship(
              {
                id: uuid.v4(),
                source_ref: targetAnnotation.id,
                target_ref: sourceAnnotation.id,
                ...nameObj,
              },
              [sourceAnnotation, targetAnnotation],
              relationship,
            )!,
          );
        }
        if (!relationship.is_directed) {
          return possibilities.slice(0, 1);
        }
        return possibilities;
      });
    },
    [relationshipDefinitions, isValidRelationshipForAnnotationPair],
  );

  const getValidRelationshipTargets = React.useCallback(
    (
      sourceAnnotation: INamedEntityRecognitionAnnotation,
      annotations: INamedEntityRecognitionAnnotation[],
      relationships: INamedEntityRecognitionRelationship[],
    ) => {
      return annotations
        .filter(annotation => {
          if (sourceAnnotation.id === annotation.id) {
            return false;
          }

          // For now, only allow one relationship between annotations.
          if (
            relationships.find(
              relationship =>
                (relationship.source_ref === sourceAnnotation.id &&
                  relationship.target_ref === annotation.id) ||
                (relationship.source_ref === annotation.id &&
                  relationship.target_ref === sourceAnnotation.id),
            )
          ) {
            return false;
          }

          return !!getValidRelationshipsForAnnotationPair(sourceAnnotation, annotation).length;
        })
        .map(annotation => annotation.id);
    },
    [getValidRelationshipsForAnnotationPair],
  );

  // Intended to be called when an annotation is modified, to check whether this relationship
  // can still exist.
  const isRelationshipStillValid = (
    relationship: INamedEntityRecognitionRelationship,
    changedAnnotationId: string,
    newAnnotationLabel: string,
  ): boolean => {
    const modifiedRelationship = _.cloneDeep(relationship);
    if (relationship.sourceAnnotation.id === changedAnnotationId) {
      modifiedRelationship.sourceAnnotation.labelId = newAnnotationLabel;
    }
    if (modifiedRelationship.targetAnnotation.id === changedAnnotationId) {
      modifiedRelationship.targetAnnotation.labelId = newAnnotationLabel;
    }

    const validRelationships = getValidRelationshipsForAnnotationPair(
      modifiedRelationship.sourceAnnotation,
      modifiedRelationship.targetAnnotation,
    );

    const matchingRelationships = validRelationships.filter(
      otherRelationship =>
        otherRelationship.name === relationship.name &&
        otherRelationship.source_ref === relationship.source_ref &&
        otherRelationship.target_ref === relationship.target_ref,
    );
    return matchingRelationships.length > 0;
  };

  return {
    getValidRelationshipsForAnnotationPair,
    getValidRelationshipTargets,
    isRelationshipStillValid,
  };
}
